import type { Column, ColumnDef } from "@tanstack/react-table";

import { ArrowDown, ArrowUp,
} from "lucide-react";
import { CircleFlag } from "react-circle-flags";

import type { RouterOutputs } from "@/lib/trpc";

import { Button } from "@/components/ui/button";

export type Users = RouterOutputs["user"]["getDetailedUsers"]["users"][number];

const sortButton = (label: string, column: Column<Users, unknown>) => {
  return (
    <Button
      variant="nav-link"
      className="justify-between rounded-none px-2 text-sm font-medium"
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      {
        column.getIsSorted() === "desc"
          ? <ArrowDown className="!size-[18px] text-orange-500" />
          : column.getIsSorted() === "asc"
            ? <ArrowUp className="!size-[18px] text-orange-500" />
            : <ArrowUp className="!size-[18px] text-orange-500 opacity-0" />
      }
    </Button>
  );
};

const headerCell = (label: string) => {
  return (
    <span className="px-2">{label}</span>
  );
};

export const columns: ColumnDef<Users>[] = [
  {
    accessorKey: "imgUrl",
    header: () => headerCell("Avatar"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <img
          src={user.imgUrl}
          alt={`${user.firstName} ${user.lastName}`}
          className="size-10 rounded-full object-cover"
        />
      );
    },
  },
  {
    accessorKey: "firstName",
    header: ({ column }) => sortButton("Prénom", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap font-medium uppercase">
          {user.firstName}
        </span>
      );
    },
  },
  {
    accessorKey: "lastName",
    header: ({ column }) => sortButton("Nom", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap font-medium uppercase">
          {user.lastName}
        </span>
      );
    },
  },
  {
    accessorKey: "role",
    header: ({ column }) => sortButton("Rôle", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
        </span>
      );
    },
  },
  {
    accessorKey: "country",
    header: ({ column }) => sortButton("Pays", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className="flex shrink-0 items-center gap-2 rounded-full bg-neutral-100 p-0.5">
          <CircleFlag countryCode={user.country.toLowerCase()} className="size-5 rounded-full" />
          <span className="pr-3 text-sm">
            {user.country.toUpperCase()}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "clubName",
    header: ({ column }) => sortButton("Club", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap uppercase">
          {user.clubName}
        </span>
      );
    },
  },
  {
    accessorKey: "email",
    header: () => headerCell("Email"),
  },
  {
    accessorKey: "clubEmail",
    header: () => headerCell("Email du club"),
  },
  {
    accessorKey: "phoneNumber",
    header: () => headerCell("Téléphone"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {user.phoneNumber}
        </span>
      );
    },
  },
  {
    accessorKey: "clubPhone",
    header: () => headerCell("Téléphone du club"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {user.clubPhone}
        </span>
      );
    },
  },
  {
    accessorKey: "clubAddress",
    header: () => headerCell("Adresse du club"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {user.clubAddress}
        </span>
      );
    },
  },
  {
    accessorKey: "clubCity",
    header: () => headerCell("Ville du club"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {user.clubCity}
        </span>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => sortButton("Date d'inscription", column),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-nowrap">
          {new Date(user.createdAt).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })}
        </span>
      );
    },
  },
];
